import type { ActiveBlock, LiveReference, PollResults, SessionState, VoteChoice } from "@hali-shares/deck-schema";
import { FREE_TEXT_MAX, normaliseText } from "@hali-shares/deck-schema";
import { useState } from "react";
import { Check } from "../../deck/PollSlide";
import { safeHref } from "../../lib/links";
import type { PhoneSession } from "../../live/usePhoneSession";

/** Picks the phone screen from absolute state: ended, references, results, vote, or lobby. */
export function PhoneLive({ code, live }: { code: string; live: PhoneSession }) {
  const { state } = live;
  if (!state) return <p className="phone-status">{live.connected ? "LOADING" : "CONNECTING"}</p>;
  const a = state.activeBlock;
  let screen;
  if (state.status === "ENDED") screen = <Ended state={state} live={live} />;
  else if (state.showReferences) screen = <References title={state.referencesTitle} refs={state.references} />;
  else if (a && a.revealed)
    screen = <Results block={a} results={live.results[a.blockId]} mine={live.acks[a.blockId]} live={live} />;
  else if (a) screen = <Vote key={a.blockId} block={a} live={live} />;
  else screen = <Lobby state={state} live={live} />;
  return (
    <>
      {!live.connected && <span className="phone-status">RECONNECTING</span>}
      <div className="phone-body" data-screen={screenName(state)} data-code={code}>
        {screen}
      </div>
    </>
  );
}

function screenName(s: SessionState): string {
  if (s.status === "ENDED") return "ended";
  if (s.showReferences) return "references";
  if (s.activeBlock?.revealed) return "results";
  if (s.activeBlock) return "vote";
  return "lobby";
}

/* ---------- lobby ---------- */

function Lobby({ state, live }: { state: SessionState; live: PhoneSession }) {
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span className="phone-small">
          {state.deckTitle}
          {state.presenter && ` · ${state.presenter}`}
        </span>
        <h1 className="phone-h phone-h-lg">You’re in.</h1>
        <p className="phone-text">Questions appear here on their own. Keep this page open.</p>
      </div>
      <div className="now-card" aria-live="polite">
        <span className="now-label">
          <span className="now-dot" />
          ON SCREEN NOW
        </span>
        <span className="now-title" data-testid="now-title">
          {state.nowTitle}
        </span>
      </div>
      <Nickname live={live} />
    </>
  );
}

/** Optional: a name for the quiz leaderboard. Without one you still play; you just don't appear on screen. */
function Nickname({ live }: { live: PhoneSession }) {
  const current = live.self?.nickname ?? null;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  if (current && !editing)
    return (
      <p className="phone-small" data-testid="nickname">
        Quiz name: <b>{current}</b>{" "}
        <button
          type="button"
          className="link-btn-phone"
          onClick={() => {
            setValue(current);
            setEditing(true);
          }}
        >
          Change
        </button>
      </p>
    );
  return (
    <form
      className="phone-field"
      onSubmit={(e) => {
        e.preventDefault();
        if (!value.trim()) return;
        live.setNickname(value.trim());
        setEditing(false);
      }}
    >
      <label htmlFor="nickname">Quiz leaderboard name (optional)</label>
      <div className="nick-row">
        <input
          id="nickname"
          className="phone-input-plain"
          value={value}
          maxLength={24}
          autoComplete="nickname"
          onChange={(e) => setValue(e.target.value)}
        />
        <button type="submit" className="phone-btn-small" disabled={!value.trim()}>
          Save
        </button>
      </div>
      {live.nicknameError && <span className="phone-small">{live.nicknameError}</span>}
    </form>
  );
}

function ScoreLine({ live }: { live: PhoneSession }) {
  const s = live.self;
  if (!s || s.quizzes === 0) return null;
  return (
    <p className="score-line" data-testid="score">
      Quiz score {s.score} of {s.quizzes}
      {s.rank !== null && ` · #${s.rank} of ${s.of}`}
      {s.nickname ? ` · on the board as ${s.nickname}` : ""}
    </p>
  );
}

/* ---------- vote ---------- */

type Option = { id: string; key: string; label: string };

function optionsOf(block: ActiveBlock): Option[] {
  if (block.kind === "scale") {
    const { min, max } = block.scale ?? { min: 1, max: 5 };
    return Array.from({ length: scaleSteps(min, max) }, (_, i) => ({
      id: String(min + i),
      key: String(min + i),
      label: "",
    }));
  }
  return block.options.map((o, i) => ({ id: o.id, key: String.fromCharCode(65 + i), label: o.label }));
}

function sameChoice(a: VoteChoice | undefined, b: VoteChoice | undefined): boolean {
  if (a === undefined || b === undefined) return false;
  if (typeof a === "string" && typeof b === "string") return normaliseText(a) === normaliseText(b);
  return JSON.stringify(a) === JSON.stringify(b);
}

function Vote({ block, live }: { block: ActiveBlock; live: PhoneSession }) {
  const acked = live.acks[block.blockId];
  const [touched, setPicked] = useState<VoteChoice | undefined>(undefined);
  // Until the phone picks something, show what the server recorded (it can arrive after a reconnect).
  const picked = touched ?? acked;
  const free = block.kind === "wordcloud" || block.kind === "open";

  const empty =
    picked === undefined ||
    (Array.isArray(picked) && picked.length === 0) ||
    (typeof picked === "string" && !normaliseText(picked));
  const canSend = !empty && !block.locked && !sameChoice(picked, acked);
  const label = block.locked
    ? "Locked"
    : acked !== undefined && sameChoice(picked, acked)
      ? "Vote sent"
      : acked !== undefined
        ? "Change vote"
        : "Send vote";

  const toggle = (o: Option) => {
    if (block.locked) return;
    if (block.kind === "multi") {
      const cur = Array.isArray(picked) ? picked : [];
      setPicked(cur.includes(o.id) ? cur.filter((x) => x !== o.id) : [...cur, o.id]);
    } else setPicked(block.kind === "scale" ? Number(o.id) : o.id);
  };
  const isOn = (o: Option) =>
    Array.isArray(picked) ? picked.includes(o.id) : picked !== undefined && String(picked) === o.id;

  return (
    <>
      <h1 className="phone-q">{block.question}</h1>
      {block.kind === "multi" && <span className="phone-small">Pick all that apply.</span>}
      {free ? (
        <FreeText
          kind={block.kind as "wordcloud" | "open"}
          value={typeof picked === "string" ? picked : ""}
          locked={block.locked}
          onChange={setPicked}
          onSubmit={() => canSend && live.vote(block.blockId, normaliseText(String(picked)))}
        />
      ) : (
        <div className="choices-wrap">
          {/* A rating scale is one row of numbers, ends labelled underneath. */}
          <div
            className="choices"
            data-scale={block.kind === "scale"}
            role={block.kind === "multi" ? "group" : "radiogroup"}
            aria-label="Options"
          >
            {optionsOf(block).map((o) => (
              <button
                key={o.id}
                type="button"
                className="choice"
                aria-pressed={isOn(o)}
                disabled={block.locked}
                onClick={() => toggle(o)}
              >
                <span className="choice-key">{o.key}</span>
                {block.kind !== "scale" && <span style={{ flex: 1 }}>{o.label}</span>}
              </button>
            ))}
          </div>
          {block.kind === "scale" && (block.scale?.minLabel || block.scale?.maxLabel) && (
            <div className="scale-ends">
              <span>{block.scale.minLabel}</span>
              <span>{block.scale.maxLabel}</span>
            </div>
          )}
        </div>
      )}
      {block.kind === "open" && acked !== undefined && <RoomAnswers block={block} live={live} mine={acked} />}
      <div className="vote-foot">
        {live.voteError && !block.locked && (
          <p className="phone-error" role="alert">
            {live.voteError}
          </p>
        )}
        {acked !== undefined && !block.locked && (
          <p className="note note-yellow" role="status">
            Vote counted. You can change it until the poll locks.
          </p>
        )}
        {block.locked && (
          <p className="note note-grey" role="status">
            Voting is locked. Results are coming.
          </p>
        )}
        <button
          type="button"
          className="phone-btn"
          disabled={!canSend}
          onClick={() =>
            picked !== undefined &&
            live.vote(block.blockId, typeof picked === "string" ? normaliseText(picked) : picked)
          }
        >
          {label}
        </button>
      </div>
    </>
  );
}

/**
 * Typing an answer. A word cloud gets one big line (Enter sends); an open question gets a few lines. Both say what
 * happens to the answer and count the characters left.
 */
function FreeText({
  kind,
  value,
  locked,
  onChange,
  onSubmit,
}: {
  kind: "wordcloud" | "open";
  value: string;
  locked: boolean;
  onChange: (v: string) => void;
  onSubmit: () => void;
}) {
  const cloud = kind === "wordcloud";
  const common = {
    "aria-label": "Your answer",
    "aria-describedby": "free-hint",
    maxLength: FREE_TEXT_MAX,
    value,
    disabled: locked,
    autoCapitalize: cloud ? "none" : "sentences",
    enterKeyHint: "send" as const,
  };
  return (
    <div className="free">
      {cloud ? (
        <input
          {...common}
          className="phone-word"
          type="text"
          autoComplete="off"
          placeholder="Type a word"
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), onSubmit())}
        />
      ) : (
        <textarea
          {...common}
          className="phone-textarea"
          placeholder="Type your question or thought"
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      <div className="free-foot">
        <span id="free-hint">
          {cloud
            ? "One or two words. The more people say the same word, the bigger it gets on screen."
            : "Short and specific works best. Others can upvote it."}
        </span>
        <span className="free-count" aria-live="polite">
          {value.length}/{FREE_TEXT_MAX}
        </span>
      </div>
    </div>
  );
}

/** Open polls, once you've answered: everyone else's answers, to upvote the ones you want asked too. */
function RoomAnswers({ block, live, mine }: { block: ActiveBlock; live: PhoneSession; mine: VoteChoice }) {
  const own = typeof mine === "string" ? mine.toLowerCase() : "";
  const answers = (live.answers[block.blockId] ?? []).filter((a) => a.key !== own);
  const upvoted = new Set(live.upvoted[block.blockId] ?? []);
  if (answers.length === 0)
    return <p className="phone-small">Other answers show up here. Upvote the ones you want asked too.</p>;
  return (
    <div className="room-answers" aria-label="Answers from the room">
      <span className="now-label">FROM THE ROOM · UPVOTE WHAT YOU WANT ASKED</span>
      {answers.slice(0, 20).map((a) => {
        const on = upvoted.has(a.key);
        return (
          <button
            key={a.key}
            type="button"
            className="room-answer"
            aria-pressed={on}
            disabled={block.locked}
            onClick={() => live.upvote(block.blockId, a.key, !on)}
          >
            <span style={{ flex: 1, textAlign: "left" }}>{a.text}</span>
            <span className="room-up">
              <svg
                aria-hidden="true"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 10V2.5M2.5 6 6 2.5 9.5 6" />
              </svg>
              {a.up ?? 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------- results ---------- */

function Results({
  block,
  results,
  mine,
  live,
}: {
  block: ActiveBlock;
  results?: PollResults;
  mine?: VoteChoice;
  live: PhoneSession;
}) {
  const total = results?.total ?? 0;
  const counts = results?.counts ?? {};
  const free = block.kind === "wordcloud" || block.kind === "open";
  const rows = optionsOf(block);
  const max = Math.max(0, ...rows.map((r) => counts[r.id] ?? 0));
  const isMine = (id: string) => (Array.isArray(mine) ? mine.includes(id) : mine !== undefined && String(mine) === id);
  const correct = results?.correctOptionId;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span className="phone-small" data-testid="results-total">
          {total} {total === 1 ? "person" : "people"} voted
        </span>
        <h1 className="phone-q">{block.question}</h1>
      </div>
      {block.type === "quiz" && correct && (
        <p className="verdict" data-testid="verdict">
          {mine === undefined ? "You didn’t answer this one." : mine === correct ? "You got it." : "Not this time."}
        </p>
      )}
      {free ? (
        <div className="links">
          {(results?.words ?? []).slice(0, 12).map((w) => (
            <div key={w.text} className="res-line" style={{ padding: "10px 0", borderTop: "1px solid var(--line)" }}>
              <span>
                {w.text}
                {typeof mine === "string" && mine.toLowerCase() === w.text.toLowerCase() && (
                  <span className="you">YOU</span>
                )}
              </span>
              <span className="res-pct">{w.n}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="res-rows">
          {rows.map((r) => {
            const n = counts[r.id] ?? 0;
            const lead = correct ? r.id === correct : n === max && max > 0;
            return (
              <div key={r.id} className="res-row" data-option={r.id}>
                <div className="res-line">
                  <span>
                    {block.kind === "scale" ? r.key : r.label}
                    {isMine(r.id) && <span className="you">YOU</span>}
                  </span>
                  <span className="res-pct">
                    {correct === r.id && <Check />}
                    {total ? Math.round((n / total) * 100) : 0}%
                  </span>
                </div>
                <div className="res-bar">
                  <div
                    className="res-fill"
                    data-lead={lead}
                    style={{ width: total ? `${Math.round((n / total) * 100)}%` : 0 }} // share of voters, like the label
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {block.type === "quiz" && results?.explanation && <p className="phone-text">{results.explanation}</p>}
      {block.type === "quiz" && <ScoreLine live={live} />}
    </>
  );
}

/* ---------- references / ended ---------- */

function LinkList({ refs }: { refs: LiveReference[] }) {
  return (
    <div className="links">
      {refs.map((r) => (
        <a key={r.id} className="link-row" href={safeHref(r.url)} target="_blank" rel="noopener noreferrer">
          <span className="link-title">{r.title}</span>
          <span className="link-meta">
            {[r.authors, [r.venue, r.year].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
          </span>
        </a>
      ))}
    </div>
  );
}

function References({ title, refs }: { title: string; refs: LiveReference[] }) {
  return (
    <>
      <h1 className="phone-h">{title}</h1>
      <p className="phone-text">Links from this talk, yours to keep.</p>
      <LinkList refs={refs} />
    </>
  );
}

function Ended({ state, live }: { state: SessionState; live: PhoneSession }) {
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span className="phone-small">
          {state.deckTitle}
          {state.presenter && ` · ${state.presenter}`}
        </span>
        <h1 className="phone-h phone-h-lg">Thanks for joining</h1>
        <p className="phone-text">The session has ended. The links stay here.</p>
        <ScoreLine live={live} />
      </div>
      {state.references.length > 0 && <LinkList refs={state.references} />}
    </>
  );
}

/** Points on a scale. Clamped: a half-edited scale (from > to, or 1..10000) mustn't break rendering. */
function scaleSteps(min: number, max: number): number {
  return Math.min(Math.max(0, max - min + 1), 21);
}
